import React, { useEffect, useState } from "react";
import { Loader2, LogOut, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminCatalogService, formatApiError } from "../../services/catalogService";
import { ICON_LIBRARY, iconFor } from "../../lib/iconLibrary";

const inputCls = "h-11 bg-white border border-black/15 px-3 text-sm focus:outline-none focus:border-[#D92A47]";

// Audit fix (e-commerce task): gestión de categorías, variedades y productos.
// Task 10 (C11): each variety carries up to 3 admin-chosen qualities (word + icon).
function CategoriesManager({ categories, varieties, onChanged }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const add = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await adminCatalogService.createCategory(label.trim());
      setLabel("");
      onChanged();
      toast.success("Collection added", { description: label.trim() });
    } catch (err) {
      toast.error("Could not add collection", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await adminCatalogService.deleteCategory(id);
      setConfirmId(null);
      onChanged();
      toast.success("Collection deleted");
    } catch (err) {
      setConfirmId(null);
      toast.error("Could not delete", { description: formatApiError(err) });
    }
  };

  return (
    <div className="bg-white border border-black/10 p-5" data-testid="catalog-categories">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">Collections</span>
      <div className="flex flex-wrap gap-2 mt-3">
        {categories.map((c) => {
          const inUse = varieties.some((v) => v.category_id === c.id);
          return (
            <span key={c.id} className="inline-flex items-center gap-1.5 border border-black/10 px-3 py-1.5 text-sm" data-testid={`category-chip-${c.id}`}>
              {c.label}
              {!inUse && (
                confirmId === c.id ? (
                  <button onClick={() => remove(c.id)} data-testid={`category-delete-confirm-${c.id}`}
                    className="text-[10px] font-semibold uppercase tracking-wide text-[#D92A47]">
                    Confirm
                  </button>
                ) : (
                  <button onClick={() => setConfirmId(c.id)} aria-label={`Delete ${c.label}`}
                    data-testid={`category-delete-${c.id}`} className="text-[#817B74] hover:text-[#D92A47]">
                    <X size={12} />
                  </button>
                )
              )}
            </span>
          );
        })}
      </div>
      <div className="flex gap-2 mt-4">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="New collection name…"
          data-testid="category-add-input"
          className={`flex-1 ${inputCls}`}
        />
        <button onClick={add} disabled={busy || !label.trim()} data-testid="category-add-btn"
          className="inline-flex items-center gap-2 h-11 px-5 bg-[#252525] text-white text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-[#D92A47] disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add
        </button>
      </div>
    </div>
  );
}

function AddVarietyForm({ categories, onCreated }) {
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [color, setColor] = useState("#E5A9BD");
  const [colors, setColors] = useState([]);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const created = await adminCatalogService.createVariety({
        name: name.trim(), category_id: categoryId, colors, description_en: "",
      });
      toast.success("Variety created", { description: `${created.name} — add its photos in the Variety photos tab` });
      setName(""); setColors([]);
      onCreated(created);
    } catch (err) {
      toast.error("Could not create variety", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-black/10 p-5" data-testid="catalog-add-variety">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">Add variety</span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Variety name…"
          data-testid="variety-add-name" className={inputCls} />
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
          data-testid="variety-add-category" className={inputCls}>
          <option value="">Choose collection…</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {colors.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 border border-black/10 px-2 py-1 text-xs">
            <span className="h-4 w-4 rounded-full ring-1 ring-black/10" style={{ backgroundColor: c }} />
            {c}
            <button onClick={() => setColors((prev) => prev.filter((x) => x !== c))} aria-label={`Remove ${c}`}><X size={12} /></button>
          </span>
        ))}
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
          data-testid="variety-add-color" className="h-9 w-12 border border-black/15 bg-white cursor-pointer" />
        <button onClick={() => !colors.includes(color.toUpperCase()) && setColors((p) => [...p, color.toUpperCase()])}
          data-testid="variety-add-color-btn"
          className="h-9 px-3 border border-black/15 text-[11px] uppercase tracking-[0.12em] hover:border-[#D92A47]">
          Add color
        </button>
      </div>
      <button onClick={create} disabled={busy || !name.trim() || !categoryId} data-testid="variety-add-submit"
        className="mt-4 inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] disabled:opacity-50">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : "Create variety"}
      </button>
    </div>
  );
}

function QualitiesEditor({ variety, onSaved }) {
  const [icon, setIcon] = useState("vase");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const qualities = variety.qualities || [];

  const save = async (next) => {
    setBusy(true);
    try {
      onSaved(await adminCatalogService.updateVariety(variety.id, { qualities: next }));
      setLabel("");
    } catch (err) {
      toast.error("Could not save qualities", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`qualities-${variety.id}`}>
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">Ideal para (max 3)</span>
      <div className="flex flex-wrap gap-2 mt-2">
        {qualities.map((q, i) => {
          const QIcon = iconFor(q.icon);
          return (
            <span key={`${q.icon}-${i}`} className="inline-flex items-center gap-2 border border-[#D92A47]/30 bg-[#D92A47]/5 px-3 py-1.5 text-sm"
              data-testid={`quality-${variety.id}-${i}`}>
              <QIcon size={14} className="text-[#D92A47]" />
              {q.label_en}
              <button onClick={() => save(qualities.filter((_, j) => j !== i))} aria-label={`Remove ${q.label_en}`}
                data-testid={`quality-remove-${variety.id}-${i}`}><X size={12} /></button>
            </span>
          );
        })}
      </div>
      {qualities.length < 3 && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <div className="flex gap-1 flex-wrap" role="listbox" aria-label="Icon library">
            {ICON_LIBRARY.map(({ key, label: iconLabel, Icon }) => (
              <button key={key} type="button" title={iconLabel} aria-label={iconLabel}
                aria-selected={icon === key}
                onClick={() => setIcon(key)}
                data-testid={`icon-${variety.id}-${key}`}
                className={`grid h-9 w-9 place-items-center border transition-colors ${
                  icon === key ? "border-[#D92A47] bg-[#D92A47] text-white" : "border-black/15 text-[#817B74] hover:border-[#D92A47]/60"
                }`}>
                <Icon size={15} />
              </button>
            ))}
          </div>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60}
            placeholder="e.g. Ideal para florero" data-testid={`quality-input-${variety.id}`}
            className={`flex-1 min-w-[180px] ${inputCls}`} />
          <button onClick={() => label.trim() && save([...qualities, { icon, label_en: label.trim() }])}
            disabled={busy || !label.trim()} data-testid={`quality-add-${variety.id}`}
            className="h-11 px-4 bg-[#252525] text-white text-[11px] font-semibold uppercase tracking-[0.12em] hover:bg-[#D92A47] disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : "Add"}
          </button>
        </div>
      )}
    </div>
  );
}

function VarietyRow({ variety, categories, onSaved, onDeleted }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const changeCategory = async (category_id) => {
    try {
      onSaved(await adminCatalogService.updateVariety(variety.id, { category_id }));
    } catch (err) {
      toast.error("Could not change collection", { description: formatApiError(err) });
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await adminCatalogService.deleteVariety(variety.id);
      toast.success("Variety deleted", { description: variety.name });
      onDeleted(variety.id);
    } catch (err) {
      toast.error("Could not delete", { description: formatApiError(err) });
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-black/10 p-5" data-testid={`catalog-variety-${variety.id}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-serif text-2xl text-[#252525]">{variety.name}</h2>
        <div className="flex items-center gap-2">
          <select value={variety.category_id} onChange={(e) => changeCategory(e.target.value)}
            data-testid={`variety-category-${variety.id}`} className={inputCls}>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          {confirmDelete ? (
            <>
              <button onClick={remove} disabled={busy} data-testid={`variety-delete-confirm-${variety.id}`}
                className="h-11 px-4 bg-[#D92A47] text-white text-[11px] font-semibold uppercase tracking-[0.12em]">
                {busy ? <Loader2 size={13} className="animate-spin" /> : "Confirm delete"}
              </button>
              <button onClick={() => setConfirmDelete(false)} className="h-11 px-3 border border-black/15 text-[11px] uppercase tracking-[0.12em]">
                Cancel
              </button>
            </>
          ) : (
            <button onClick={() => setConfirmDelete(true)} data-testid={`variety-delete-${variety.id}`}
              className="grid h-11 w-11 place-items-center border border-black/15 text-[#817B74] hover:border-[#D92A47] hover:text-[#D92A47]"
              aria-label={`Delete ${variety.name}`}>
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>
      <QualitiesEditor variety={variety} onSaved={onSaved} />
    </section>
  );
}

export default function CatalogPanel({ admin, onLogout }) {
  const [categories, setCategories] = useState(null);
  const [varieties, setVarieties] = useState(null);

  const reload = () => {
    Promise.all([adminCatalogService.listCategories(), adminCatalogService.listVarieties()])
      .then(([cats, vars]) => { setCategories(cats); setVarieties(vars); })
      .catch((e) => toast.error("Could not load catalog", { description: formatApiError(e) }));
  };

  useEffect(() => { reload(); }, []);

  const onSaved = (updated) => setVarieties((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
  const onCreated = (created) => setVarieties((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
  const onDeleted = (id) => setVarieties((prev) => prev.filter((v) => v.id !== id));

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-catalog-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Catalog management</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Collections, varieties and their "ideal para" qualities. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {categories === null || varieties === null ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
      ) : (
        <div className="space-y-6">
          <CategoriesManager categories={categories} varieties={varieties} onChanged={reload} />
          <AddVarietyForm categories={categories} onCreated={onCreated} />
          {varieties.map((v) => (
            <VarietyRow key={v.id} variety={v} categories={categories} onSaved={onSaved} onDeleted={onDeleted} />
          ))}
        </div>
      )}
    </div>
  );
}
